import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { DealStatus, FrontEndType, Line } from 'components/common/types/Deal.types';
import { FeatureFlags } from 'components/common/types/Features.types';
import { Store } from 'components/common/types/Store.types';
import { getDealWithLineFormData } from 'components/pages/DealWithLines/DealLines/transformPostData';
import { useHasFeatureAccess } from 'customHooks/useHasFeatureAccess';

interface UseAvailabilityFormDataResult {
  buildFormData: ({ currentLineOverride }?: { currentLineOverride?: Partial<Line> }) => FormData;
  isDealForm: boolean;
}

export const useAvailabilityFormData = (): UseAvailabilityFormDataResult => {
  const environmentId = useSelector((state: Store) => state.environment.environmentId);
  const programmatic = useSelector((state: Store) => state.dealManagement.programmatic);
  const commonDeal = useSelector((state: Store) => state.dealManagement.commonDeal);
  const routeFrameCodes = useSelector((state: Store) => state.dealManagement.commonDeal.currentLine.routeFrameCodes);
  const frontEndType = useSelector((state: Store) => state.dealManagement.commonDeal.currentLine.frontEndType);
  const isClientSideAssetFiltering = useSelector(
    (state: Store) => state.dealManagement.plannerSettings.isClientSideAssetFiltering,
  );
  const isDealForm = frontEndType === FrontEndType.STANDARD;
  const hasAdsDealLevelCPMEnabled = useHasFeatureAccess(FeatureFlags.DEAL_LEVEL_CPM);

  const buildFormData = useCallback(
    ({ currentLineOverride }: { currentLineOverride?: Partial<Line> } = {}) => {
      const isRejected = commonDeal.bookingStatusCode === DealStatus.REJECTED;

      const currentLine = {
        ...commonDeal.currentLine,
        proximity: {
          ...commonDeal.currentLine.proximity,
          plannerPoi: [],
          openStreetMapPoi: [],
        },
        uploadedFrameLists: [],
        routeFrameCodes: isDealForm || !isClientSideAssetFiltering ? routeFrameCodes : [],
        mediaOwners: commonDeal.currentLine.mediaOwnerMeta?.map(({ code }) => code) || [],
        ...currentLineOverride,
      };

      return getDealWithLineFormData({
        ...commonDeal,
        ...programmatic,
        currentLine,
        bookingStatusCode: isRejected ? DealStatus.REJECTED : undefined,
        hasAdsDealLevelCPMEnabled,
        environmentId,
      });
    },
    [
      commonDeal,
      programmatic,
      routeFrameCodes,
      isDealForm,
      isClientSideAssetFiltering,
      hasAdsDealLevelCPMEnabled,
      environmentId,
    ],
  );

  return {
    buildFormData,
    isDealForm,
  };
};
